import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, AlertCircle, BarChart2 } from 'lucide-react';
import { visitsApi } from '../services/api';
import { VISIT_RESPONSES, normalisedVisitResponse } from '../utils/visitResponses';

interface SummaryRow {
    user_name: string;
    response: string;
    total: number;
}

interface UserRow {
    user: string;
    counts: Map<string, number>;
    total: number;
}

const NO_RESPONSE = 'No response';

/**
 * How each rep's completed visits broke down by the answer they recorded.
 *
 * The counts come from the server aggregated over every visit the filters match.
 * Counting rows in the browser would only ever describe the page on screen, which
 * for a performance view is worse than showing nothing.
 */
const VisitPerformance: React.FC = () => {
    const [rows, setRows] = useState<SummaryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState({ date_from: '', date_to: '', user_name: '', search: '' });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await visitsApi.getResponseSummary({ ...filters, status: 'Completed' });
            if (!res?.success || !Array.isArray(res.data)) throw new Error('The server returned no summary.');
            setRows(res.data);
        } catch (e: any) {
            // An empty report and a failed one look identical once the rows are
            // gone, so the failure has to say so rather than read as "no visits".
            setError(e?.message || 'Could not load the summary.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    // Retired answers ('Happy', 'Business Close') and case drift ('Not interested')
    // are folded onto the current spelling, so one rep's work is not split across
    // two columns. Anything with no match keeps its own column instead of being
    // dropped, so each row still adds up to the visits that rep actually closed.
    const byUser = new Map<string, Map<string, number>>();
    const extras = new Set<string>();
    rows.forEach(r => {
        const user = r.user_name || '(unassigned)';
        const label = r.response ? normalisedVisitResponse(r.response) : NO_RESPONSE;
        if (label !== NO_RESPONSE && !VISIT_RESPONSES.some(v => v === label)) extras.add(label);
        if (!byUser.has(user)) byUser.set(user, new Map());
        const m = byUser.get(user)!;
        m.set(label, (m.get(label) || 0) + r.total);
    });

    const columns = [...VISIT_RESPONSES, ...Array.from(extras).sort(), NO_RESPONSE];
    const users: UserRow[] = Array.from(byUser.entries())
        .map(([user, counts]) => ({ user, counts, total: Array.from(counts.values()).reduce((a, b) => a + b, 0) }))
        .sort((a, b) => b.total - a.total);
    const columnTotal = (col: string) => users.reduce((sum, u) => sum + (u.counts.get(col) || 0), 0);
    const grandTotal = users.reduce((sum, u) => sum + u.total, 0);

    const setFilter = (key: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setFilters(f => ({ ...f, [key]: e.target.value }));

    return (
        <div className="p-3 md:p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
                    <BarChart2 className="h-5 w-5 text-blue-600" />
                    Visit Performance
                </h1>
                <button
                    onClick={load}
                    className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="block">
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">From</span>
                    <input type="date" value={filters.date_from} onChange={setFilter('date_from')} className="w-full h-9 px-2 border border-gray-300 rounded-lg text-sm" />
                </label>
                <label className="block">
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">To</span>
                    <input type="date" value={filters.date_to} onChange={setFilter('date_to')} className="w-full h-9 px-2 border border-gray-300 rounded-lg text-sm" />
                </label>
                <label className="block">
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">User</span>
                    <input type="text" value={filters.user_name} onChange={setFilter('user_name')} placeholder="All users" className="w-full h-9 px-2 border border-gray-300 rounded-lg text-sm" />
                </label>
                <label className="block">
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Search</span>
                    <input type="text" value={filters.search} onChange={setFilter('search')} placeholder="Customer or user" className="w-full h-9 px-2 border border-gray-300 rounded-lg text-sm" />
                </label>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="py-16 text-center text-gray-400">
                        <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                        <p className="text-sm font-bold uppercase tracking-widest">Loading summary…</p>
                    </div>
                ) : error ? (
                    <div className="py-12 px-4 text-center">
                        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-red-600">{error}</p>
                        <p className="text-xs text-gray-500 mt-1">This is a load failure, not an empty report.</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="py-12 text-center text-gray-400 text-sm font-medium">No completed visits match these filters.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="px-3 py-2 text-left border-r sticky left-0 bg-gray-50">User</th>
                                    {columns.map(col => <th key={col} className="px-3 py-2 text-center border-r whitespace-nowrap">{col}</th>)}
                                    <th className="px-3 py-2 text-center">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {users.map(u => (
                                    <tr key={u.user} className="hover:bg-blue-50/50">
                                        <td className="px-3 py-1.5 border-r font-semibold text-gray-800 whitespace-nowrap sticky left-0 bg-white">{u.user}</td>
                                        {columns.map(col => {
                                            const n = u.counts.get(col) || 0;
                                            return <td key={col} className={`px-3 py-1.5 border-r text-center ${n ? 'text-gray-800 font-medium' : 'text-gray-300'}`}>{n || '·'}</td>;
                                        })}
                                        <td className="px-3 py-1.5 text-center font-bold text-blue-600">{u.total}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                <tr>
                                    <td className="px-3 py-2 border-r font-bold text-gray-600 uppercase text-[11px] tracking-wider sticky left-0 bg-gray-50">All</td>
                                    {columns.map(col => <td key={col} className="px-3 py-2 border-r text-center font-bold text-gray-700">{columnTotal(col) || '·'}</td>)}
                                    <td className="px-3 py-2 text-center font-bold text-blue-700">{grandTotal}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisitPerformance;
